import React, { useCallback, useEffect, useState } from 'react';
import { defaultRange, downloadReportFile, getTeamUsage, type CustomerReport } from '../../../api/reportingApi';

/** Organisation usage report (SRS #81): who on the team actually uses the platform. */
const TeamUsagePanel: React.FC = () => {
  const [range, setRange] = useState(defaultRange());
  const [report, setReport] = useState<CustomerReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    getTeamUsage(range).then(setReport).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load')).finally(() => setLoading(false));
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const border = 'var(--ds-border, var(--ds-border))';
  const muted = 'var(--color-text-muted, var(--ds-text-muted))';
  const text = 'var(--color-text, var(--ds-text))';
  const input: React.CSSProperties = { padding: '5px 8px', borderRadius: 6, border: `1px solid ${border}`, fontSize: 12.5, background: 'var(--ds-surface, #fff)', color: text };
  const btn: React.CSSProperties = { padding: '5px 10px', borderRadius: 6, border: `1px solid ${border}`, background: 'var(--ds-surface, #fff)', cursor: 'pointer', fontSize: 12.5, color: text };
  const t = report?.totals;

  return (
    <section style={{ border: `1px solid ${border}`, borderRadius: 14, padding: 18, background: 'var(--ds-surface, #fff)', marginTop: 18 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: text }}>Usage report</div>
          <div style={{ fontSize: 12.5, color: muted }}>Who on the team signed in and what they used, for the period.</div>
        </div>
        <input type="date" style={input} value={range.since} onChange={(e) => setRange((r) => ({ ...r, since: e.target.value }))} />
        <span style={{ color: muted, fontSize: 12 }}>to</span>
        <input type="date" style={input} value={range.until} onChange={(e) => setRange((r) => ({ ...r, until: e.target.value }))} />
        <button type="button" style={btn} onClick={() => downloadReportFile('usage', 'csv', range).catch((e) => setError(e.message))}>CSV</button>
        <button type="button" style={{ ...btn, background: 'var(--ds-brand-blue)', color: 'var(--ds-text-on-dark)', border: 'none', fontWeight: 600 }} onClick={() => downloadReportFile('usage', 'pdf', range).catch((e) => setError(e.message))}>PDF report</button>
      </div>
      {error && <div style={{ color: 'var(--ds-danger-text)', fontSize: 13, marginBottom: 8 }}>{error}</div>}
      {loading && !report && <div style={{ color: muted, fontSize: 13 }}>Loading…</div>}
      {report && t && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 14 }}>
            {[
              ['Seats', `${report.seats.used}${report.seats.limit != null ? ` / ${report.seats.limit}` : ''}`],
              ['Members signed in', `${t.active_members} of ${t.members}`],
              ['Adoption', `${t.adoption_rate}%`],
              ['Searches', t.searches], ['Chat messages', t.chat_messages], ['Reports', t.reports], ['Assessments', t.assessments],
            ].map(([label, value]) => (
              <div key={String(label)} style={{ border: `1px solid ${border}`, borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: text }}>{value}</div>
                <div style={{ fontSize: 11.5, color: muted }}>{label}</div>
              </div>
            ))}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ color: muted, textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {['Member', 'Role', 'Logins', 'Last sign-in', 'Searches', 'Compar.', 'Chat msgs', 'Reports', 'Alerts', 'Assess.', 'Activity'].map((h) => <th key={h} style={{ padding: '6px 8px', borderBottom: `1px solid ${border}`, whiteSpace: 'nowrap' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {report.members.map((m) => (
                  <tr key={m.email} style={{ opacity: m.active ? 1 : 0.55 }}>
                    <td style={{ padding: '7px 8px', borderBottom: `1px solid ${border}`, color: text }}>
                      <div style={{ fontWeight: 600 }}>{m.name || m.email}</div>
                      <div style={{ fontSize: 11.5, color: muted }}>{m.email}{!m.has_login && ' · no login'}{!m.active && ' · inactive'}</div>
                    </td>
                    <td style={{ padding: '7px 8px', borderBottom: `1px solid ${border}`, color: muted }}>{m.role}</td>
                    <td style={{ padding: '7px 8px', borderBottom: `1px solid ${border}`, color: text }}>{m.logins}</td>
                    <td style={{ padding: '7px 8px', borderBottom: `1px solid ${border}`, color: muted, whiteSpace: 'nowrap' }}>{m.last_login ? new Date(m.last_login).toLocaleDateString() : 'never'}</td>
                    {[m.searches, m.comparisons, m.chat_messages, m.reports, m.alerts, m.assessments].map((v, i) => <td key={i} style={{ padding: '7px 8px', borderBottom: `1px solid ${border}`, color: text }}>{v}</td>)}
                    <td style={{ padding: '7px 8px', borderBottom: `1px solid ${border}`, color: text, fontWeight: 700 }}>{m.activity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
};

export default TeamUsagePanel;
